import type { FieldValue } from 'features/libra/ontology/ontologyFieldTypes'

export const MISSING_VALUE_PLACEHOLDER = '—'

/**
 * Renders a FieldValue as a single-line human-readable string for display in
 * the diff drawer. Not intended for serialization — pure UI affordance.
 *
 * `FieldValue` wraps the raw data under `.value`; this helper unwraps and
 * formats.
 */
export function formatFieldValue(fv: FieldValue | undefined): string {
  if (fv === null || fv === undefined) return MISSING_VALUE_PLACEHOLDER
  return formatRaw(fv.value)
}

/**
 * Exported so the drawer can compare raw inner values (e.g. for deep equality
 * against another FieldValue.value) without duplicating the format logic.
 */
export function formatRaw(value: unknown): string {
  if (value === null || value === undefined) return MISSING_VALUE_PLACEHOLDER

  if (typeof value === 'boolean') return value ? 'Yes' : 'No'

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return MISSING_VALUE_PLACEHOLDER
    return String(value)
  }

  if (typeof value === 'string') {
    if (value.trim() === '') return MISSING_VALUE_PLACEHOLDER
    return value
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return MISSING_VALUE_PLACEHOLDER
    // Arrays of all-primitive entries stay comma-joined (human-friendly for
    // string lists like `Recommendations`). Any nested object/array pushes the
    // whole array into indented JSON so deeply-nested rows (e.g. Inspection
    // History) become readable in the diff drawer.
    const hasNested = value.some(
      (item) => item !== null && typeof item === 'object'
    )
    if (hasNested) return JSON.stringify(value, null, 2)
    return value.map((item) => formatRaw(item)).join(', ')
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2)
    } catch {
      return String(value)
    }
  }

  return String(value)
}
